import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

const GLBViewer = ({ glbUrl }) => {
  const containerRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    if (!glbUrl) {
      setLoading(false);
      setError("3D model URL is not available.");
      return;
    }

    let animationFrameId;
    let model = null;

    setLoading(true);
    setError("");

    // =====================================================
    // SCENE
    // =====================================================

    const scene = new THREE.Scene();

    // =====================================================
    // CAMERA
    // =====================================================

    const width = container.clientWidth || 600;
    const height = container.clientHeight || 500;

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.01, 1000);

    camera.position.set(0, 0, 5);

    // =====================================================
    // RENDERER
    // =====================================================

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
    });

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    renderer.setSize(width, height);

    renderer.setClearColor(0xf5f5f5, 1);

    container.appendChild(renderer.domElement);

    // =====================================================
    // LIGHTING
    // =====================================================

    const ambientLight = new THREE.AmbientLight(0xffffff, 2);

    scene.add(ambientLight);

    const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0x444444, 2);

    scene.add(hemisphereLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 4);

    directionalLight.position.set(5, 5, 5);

    scene.add(directionalLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 2);

    fillLight.position.set(-5, 2, 5);

    scene.add(fillLight);

    // =====================================================
    // GLTF LOADER
    // =====================================================

    const loader = new GLTFLoader();

    console.log("[GLBViewer] Loading:", glbUrl);

    loader.load(
      glbUrl,

      // ===================================================
      // SUCCESS
      // ===================================================

      (gltf) => {
        console.log("[GLBViewer] GLB loaded successfully", gltf);

        model = gltf.scene;

        // -------------------------------------------------
        // Enable shadows / materials
        // -------------------------------------------------

        model.traverse((child) => {
          if (child.isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;

            if (child.material) {
              child.material.needsUpdate = true;
            }
          }
        });

        // -------------------------------------------------
        // Add model to scene
        // -------------------------------------------------

        scene.add(model);

        // -------------------------------------------------
        // Calculate bounding box
        // -------------------------------------------------

        const box = new THREE.Box3().setFromObject(model);

        const size = box.getSize(new THREE.Vector3());

        const center = box.getCenter(new THREE.Vector3());

        console.log("[GLBViewer] Model size:", size);

        console.log("[GLBViewer] Model center:", center);

        // -------------------------------------------------
        // Center model
        // -------------------------------------------------

        model.position.x -= center.x;
        model.position.y -= center.y;
        model.position.z -= center.z;

        // -------------------------------------------------
        // Calculate maximum dimension
        // -------------------------------------------------

        const maxDimension = Math.max(size.x, size.y, size.z);

        if (!Number.isFinite(maxDimension) || maxDimension <= 0) {
          setLoading(false);
          setError("Invalid or empty 3D model.");

          return;
        }

        // -------------------------------------------------
        // Automatic scale
        // -------------------------------------------------

        const targetSize = 3;

        const scale = targetSize / maxDimension;

        model.scale.setScalar(scale);

        // -------------------------------------------------
        // Recalculate bounding box after scaling
        // -------------------------------------------------

        const scaledBox = new THREE.Box3().setFromObject(model);

        const scaledSize = scaledBox.getSize(new THREE.Vector3());

        const scaledCenter = scaledBox.getCenter(new THREE.Vector3());

        console.log("[GLBViewer] Scaled size:", scaledSize);

        // -------------------------------------------------
        // Center again after scaling
        // -------------------------------------------------

        model.position.x -= scaledCenter.x;

        model.position.y -= scaledCenter.y;

        model.position.z -= scaledCenter.z;

        // -------------------------------------------------
        // Camera positioning
        // -------------------------------------------------

        const finalMaxDimension = Math.max(
          scaledSize.x,
          scaledSize.y,
          scaledSize.z,
        );

        const fov = camera.fov * (Math.PI / 180);

        let cameraDistance = finalMaxDimension / (2 * Math.tan(fov / 2));

        // Add some padding
        cameraDistance *= 1.5;

        camera.position.set(0, 0, cameraDistance);

        camera.lookAt(0, 0, 0);

        // -------------------------------------------------
        // Update camera clipping
        // -------------------------------------------------

        camera.near = Math.max(0.01, cameraDistance / 100);

        camera.far = Math.max(100, cameraDistance * 100);

        camera.updateProjectionMatrix();

        setLoading(false);
        setError("");
      },

      // ===================================================
      // PROGRESS
      // ===================================================

      (progress) => {
        if (progress.total) {
          const percent = (progress.loaded / progress.total) * 100;

          console.log(`[GLBViewer] Loading ${percent.toFixed(0)}%`);
        }
      },

      // ===================================================
      // ERROR
      // ===================================================

      (err) => {
        console.error("[GLBViewer] GLB loading failed:", err);

        setLoading(false);

        setError("Failed to load 3D model.");
      },
    );

    // =====================================================
    // ANIMATION
    // =====================================================

    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);

      renderer.render(scene, camera);
    };

    animate();

    // =====================================================
    // RESIZE
    // =====================================================

    const handleResize = () => {
      if (!container) return;

      const newWidth = container.clientWidth;

      const newHeight = container.clientHeight;

      if (newWidth <= 0 || newHeight <= 0) {
        return;
      }

      camera.aspect = newWidth / newHeight;

      camera.updateProjectionMatrix();

      renderer.setSize(newWidth, newHeight);
    };

    window.addEventListener("resize", handleResize);

    // =====================================================
    // CLEANUP
    // =====================================================

    return () => {
      cancelAnimationFrame(animationFrameId);

      window.removeEventListener("resize", handleResize);

      // Dispose model
      if (model) {
        model.traverse((child) => {
          if (child.isMesh) {
            child.geometry?.dispose();

            if (Array.isArray(child.material)) {
              child.material.forEach((material) => {
                material?.dispose();
              });
            } else {
              child.material?.dispose();
            }
          }
        });

        scene.remove(model);
      }

      // Dispose renderer
      renderer.dispose();

      // Remove canvas
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [glbUrl]);

  // =======================================================
  // UI
  // =======================================================

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "500px",
        background: "#f5f5f5",
        overflow: "hidden",
        borderRadius: "8px",
      }}
    >
      {/* Three.js container */}
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
        }}
      />

      {/* Loading */}
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(245,245,245,0.9)",
            zIndex: 10,
            fontSize: "16px",
            color: "#333",
          }}
        >
          Loading 3D model...
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: "8px",
            background: "#f5f5f5",
            zIndex: 10,
            color: "#d32f2f",
            fontSize: "16px",
            textAlign: "center",
            padding: "20px",
          }}
        >
          <strong>3D Model Unavailable</strong>

          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default GLBViewer;
